import { AnimatePresence, MotiView } from "moti";
import { StyleSheet, View } from "react-native";
import { Bell } from "lucide-react";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import { EmailSmsPreferences } from "./components/EmailSmsPreferences";
import { ScheduleAndSnoozePreferences } from "./components/ScheduleAndSnoozePreferences";
import { NudgesPreferences } from "./components/NudgesPreferences";
import { type NotificationCategoryKey, useNotificationPreferencesState } from "./hooks/useNotificationPreferencesState";

const SECTION_TRANSITION_DURATION_MS = 260;

export const NotificationSettingsContainer = () => {
  const {
    emailEnabled,
    textMessagesEnabled,
    activeSmsPhoneNumber,
    smsConsent,
    smsPhoneNumber,
    smsCountryCode,
    isSmsPhoneVerified,
    frequency,
    maxEmailPerDay,
    maxSmsPerDay,
    preferredSendTime,
    timezone,
    snoozeAllUntil,
    categoryToggles,
    animationsEnabled,
    activeSnooze,
    areAllNudgeCategoriesDisabled,
    shouldHoldInitialRender,
    enableAnimations,
    handlePhoneVerificationSuccess,
    setSmsPhoneNumber,
    handleEmailNotificationsToggle,
    handleTextMessagesToggle,
    handleSmsConsentChange,
    handleFrequencyChange,
    handleEmailMaxPerDayChange,
    handleSmsMaxPerDayChange,
    handlePreferredSendTimeChange,
    handleTimezoneChange,
    handlePresetSnoozeOptionPress,
    handleCustomSnoozeSelect,
    handleResumeSnooze,
    handleCategoryToggle,
  } = useNotificationPreferencesState();
  const shouldAnimateSectionTransitions = animationsEnabled;
  const shouldDisableSectionInteractions = areAllNudgeCategoriesDisabled;

  if (shouldHoldInitialRender) {
    return (
      <View style={styles.outerContainer}>
        <View style={styles.sectionSkeleton} />
        <View style={styles.sectionSkeleton} />
      </View>
    );
  }

  return (
    <View style={styles.outerContainer}>
      <NudgesPreferences
        categoryToggles={categoryToggles}
        animationsEnabled={animationsEnabled}
        onUserInteraction={enableAnimations}
        onCategoryToggle={(key, nextValue) => {
          handleCategoryToggle(key as NotificationCategoryKey, nextValue);
        }}
      />

      <AnimatePresence>
        {areAllNudgeCategoriesDisabled ? (
          <MotiView
            key="notification-categories-info-box"
            from={shouldAnimateSectionTransitions ? { opacity: 0, translateY: -6 } : undefined}
            animate={{ opacity: 1, translateY: 0 }}
            exit={shouldAnimateSectionTransitions ? { opacity: 0, translateY: -6 } : undefined}
            transition={{
              type: "timing",
              duration: shouldAnimateSectionTransitions ? SECTION_TRANSITION_DURATION_MS : 0,
            }}>
            <View style={styles.infoBox}>
              <Bell size={14} color={colors.warning[600]} />
              <CustomText
                text="Enable at least one notification category above to start receiving notifications."
                useTranslationText={false}
                style={styles.infoBoxText}
              />
            </View>
          </MotiView>
        ) : null}
      </AnimatePresence>

      <MotiView
        animate={{ opacity: shouldDisableSectionInteractions ? 0.45 : 1 }}
        transition={{
          type: "timing",
          duration: shouldAnimateSectionTransitions ? SECTION_TRANSITION_DURATION_MS : 0,
        }}>
        <View pointerEvents={shouldDisableSectionInteractions ? "none" : "auto"} style={styles.mutedSectionGroup}>
          <EmailSmsPreferences
            emailEnabled={emailEnabled}
            textMessagesEnabled={textMessagesEnabled}
            activeVerifiedSmsPhoneNumber={activeSmsPhoneNumber}
            smsConsent={smsConsent}
            smsPhoneNumber={smsPhoneNumber}
            smsCountryCode={smsCountryCode}
            isSmsPhoneVerified={isSmsPhoneVerified}
            onPhoneVerificationSuccess={handlePhoneVerificationSuccess}
            frequencyValue={String(frequency)}
            emailMaxPerDayValue={`${maxEmailPerDay}`}
            smsMaxPerDayValue={`${maxSmsPerDay}`}
            animationsEnabled={animationsEnabled}
            onUserInteraction={enableAnimations}
            onEmailNotificationsToggle={handleEmailNotificationsToggle}
            onTextMessagesToggle={handleTextMessagesToggle}
            onSmsConsentChange={handleSmsConsentChange}
            onFrequencyChange={handleFrequencyChange}
            onEmailMaxPerDayChange={handleEmailMaxPerDayChange}
            onSmsMaxPerDayChange={handleSmsMaxPerDayChange}
            onSmsPhoneNumberChange={setSmsPhoneNumber}
          />

          <ScheduleAndSnoozePreferences
            preferredSendTime={preferredSendTime}
            timezone={timezone}
            activeSnooze={activeSnooze}
            snoozeAllUntil={snoozeAllUntil}
            animationsEnabled={animationsEnabled}
            onUserInteraction={enableAnimations}
            onPreferredSendTimeChange={handlePreferredSendTimeChange}
            onTimezoneChange={handleTimezoneChange}
            onPresetSnoozeSelect={handlePresetSnoozeOptionPress}
            onCustomSnoozeSelect={handleCustomSnoozeSelect}
            onResumeSnooze={handleResumeSnooze}
          />
        </View>
      </MotiView>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    gap: spacing.sm,
  },
  mutedSectionGroup: {
    gap: spacing.sm,
  },
  sectionSkeleton: {
    height: 320,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
  },
  infoBox: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgb(253, 230, 138)",
    backgroundColor: "rgb(255, 251, 235)",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  infoBoxText: {
    flex: 1,
    color: colors.warning[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
});
